import { ChevronLeft, ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "../lib/cn";
import { useUiText } from "../lib/locale";
import { Button, IconButton } from "./button";
import { Checkbox } from "./choice";
import { Skeleton } from "./display";

export type Column<T> = {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  className?: string;
  align?: "start" | "end" | "center";
  /** Роль колонки в карточке на mobile: title — заголовок, subtitle — под ним, meta — строка «подпись: значение», hidden — не показывать. */
  mobile?: "title" | "subtitle" | "meta" | "hidden";
};

export type DataTableProps<T> = {
  rows: T[] | undefined;
  columns: Column<T>[];
  rowKey: (row: T) => string;
  loading?: boolean;
  /** Пустое состояние (без данных / ничего не найдено). */
  empty?: ReactNode;
  onRowClick?: (row: T) => void;
  /** Кнопки действий строки (Menu «⋯»). */
  actions?: (row: T) => ReactNode;
  /** Чек-лист: выбранные ключи строк (rowKey) и их изменение — колонка с чекбоксами и «выбрать все». */
  selected?: string[];
  onSelectedChange?: (keys: string[]) => void;
  className?: string;
};

const alignCls = { start: "text-start", end: "text-end", center: "text-center" } as const;

/** Таблица на md+, карточки на mobile (ТЗ 12 §2: «таблицы на mobile превращаются в карточки»). */
export function DataTable<T>({ rows, columns, rowKey, loading, empty, onRowClick, actions, selected, onSelectedChange, className }: DataTableProps<T>) {
  const text = useUiText();
  if (loading && !rows) {
    return (
      <div className={cn("flex flex-col gap-2", className)} aria-busy>
        {Array.from({ length: 5 }, (_, i) => <Skeleton key={i} className="h-14" />)}
      </div>
    );
  }
  if (!rows?.length) return <>{empty}</>;

  const title = columns.find((c) => c.mobile === "title") ?? columns[0]!;
  const subtitle = columns.filter((c) => c.mobile === "subtitle");
  const meta = columns.filter((c) => c !== title && !subtitle.includes(c) && c.mobile !== "hidden");
  const selectable = !!onSelectedChange;
  const picked = new Set(selected ?? []);
  const keys = rows.map(rowKey);
  const pickedHere = keys.filter((k) => picked.has(k)).length;
  const toggle = (k: string, on: boolean) => onSelectedChange?.(on ? [...picked, k] : [...picked].filter((x) => x !== k));
  const toggleAll = (on: boolean) => onSelectedChange?.(on ? [...new Set([...picked, ...keys])] : [...picked].filter((x) => !keys.includes(x)));
  const allState = pickedHere === 0 ? false : pickedHere === keys.length ? true : "indeterminate";
  const rowBox = (row: T) => {
    const k = rowKey(row);
    return <Checkbox checked={picked.has(k)} onCheckedChange={(on) => toggle(k, on)} aria-label={text.selectRow} />;
  };

  return (
    <div className={cn(loading && "opacity-60 transition-opacity", className)}>
      {/* mobile: карточки */}
      {selectable && (
        <div className="border-b border-border py-2 md:hidden">
          <Checkbox checked={allState} onCheckedChange={toggleAll} label={text.selectAll} />
        </div>
      )}
      <ul className="flex flex-col divide-y divide-border md:hidden">
        {rows.map((row) => (
          <li key={rowKey(row)} className="flex items-start gap-3 py-3">
            {selectable && <span className="grid min-h-11 min-w-11 shrink-0 place-items-center -my-2.5 -ms-2.5">{rowBox(row)}</span>}
            <button
              type="button"
              disabled={!onRowClick}
              onClick={() => onRowClick?.(row)}
              className="flex min-w-0 flex-1 flex-col gap-1 text-start disabled:cursor-default"
            >
              <span className="font-semibold">{title.cell(row)}</span>
              {subtitle.map((c) => <span key={c.key} className="text-sm text-muted">{c.cell(row)}</span>)}
              {meta.length > 0 && (
                <dl className="mt-1 grid grid-cols-[fit-content(50%)_minmax(0,1fr)] gap-x-3 gap-y-1 text-sm">
                  {meta.map((c) => (
                    <div key={c.key} className="contents">
                      <dt className="text-subtle">{c.header}</dt>
                      <dd className="min-w-0 break-words">{c.cell(row)}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </button>
            {actions && <div className="shrink-0">{actions(row)}</div>}
          </li>
        ))}
      </ul>
      {/* md+: таблица */}
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b-2 border-border text-xs font-medium tracking-wide text-subtle uppercase">
              {selectable && (
                <th className="w-12 ps-4">
                  <Checkbox checked={allState} onCheckedChange={toggleAll} aria-label={text.selectAll} />
                </th>
              )}
              {columns.map((c) => (
                <th key={c.key} scope="col" className={cn("px-4 py-2.5 font-medium whitespace-nowrap", alignCls[c.align ?? "start"], c.className)}>
                  {c.header}
                </th>
              ))}
              {actions && <th className="w-12" aria-label={text.actions} />}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={rowKey(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cn("border-b border-border last:border-0", onRowClick && "cursor-pointer hover:bg-surface-2", picked.has(rowKey(row)) && "bg-primary-soft/40")}
              >
                {selectable && <td className="w-12 ps-4" onClick={(e) => e.stopPropagation()}>{rowBox(row)}</td>}
                {columns.map((c) => (
                  <td key={c.key} className={cn("px-4 py-4 leading-[1.4]", alignCls[c.align ?? "start"], c.className)}>{c.cell(row)}</td>
                ))}
                {actions && <td className="px-2 py-2 text-end" onClick={(e) => e.stopPropagation()}>{actions(row)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Панель массовых действий над выбранными записями: «Выбрано: N», действия, «Снять выбор». Липнет к низу экрана. */
export function SelectionBar({ count, onClear, children, className }: { count: number; onClear: () => void; children?: ReactNode; className?: string }) {
  const text = useUiText();
  if (!count) return null;
  return (
    <div role="toolbar" aria-label={text.selectedCount(count)} className={cn("sticky bottom-3 z-20 flex flex-wrap items-center gap-2 rounded-card border border-border bg-surface p-2 ps-4 shadow-[0_12px_32px_-12px_rgb(25_25_28/0.28)] max-md:bottom-[calc(env(safe-area-inset-bottom)+76px)]", className)}>
      <span className="me-auto text-sm font-semibold tabular-nums">{text.selectedCount(count)}</span>
      {children}
      <Button size="sm" variant="ghost" onClick={onClear}>{text.clearSelection}</Button>
    </div>
  );
}

export function Pagination({ page, pageSize, total, onPageChange, className }: { page: number; pageSize: number; total: number; onPageChange: (p: number) => void; className?: string }) {
  const text = useUiText();
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (total <= pageSize) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  return (
    <nav aria-label={text.pages} className={cn("flex items-center justify-between gap-3 text-sm text-muted", className)}>
      <span>{text.pageRange(from, to, total)}</span>
      <div className="flex items-center gap-1">
        <IconButton aria-label={text.prevPage} variant="outline" size="sm" icon={<ChevronLeft />} disabled={page <= 1} onClick={() => onPageChange(page - 1)} />
        <span className="min-w-16 text-center text-fg">{page} / {pages}</span>
        <IconButton aria-label={text.nextPage} variant="outline" size="sm" icon={<ChevronRight />} disabled={page >= pages} onClick={() => onPageChange(page + 1)} />
      </div>
    </nav>
  );
}

/** Заголовок страницы: на mobile действия переносятся под заголовок. */
export function PageHeader({ title, description, actions, className }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; className?: string }) {
  return (
    <div className={cn("flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between", className)}>
      <div className="min-w-0">
        <h1 className="text-xl leading-normal font-semibold sm:text-2xl">{title}</h1>
        {description && <p className="mt-1 text-sm text-muted">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2 [&>*]:max-sm:flex-1">{actions}</div>}
    </div>
  );
}
